/**
 * A geometria do modelo 3D da campanha CPTu (onda 316, Tarefa 316.3; spec 07 §5.7 e §13) — contas puras, sem
 * DOM e SEM o three.js: os buffers (posições, UV, índices, cores RGBA) que o `Modelo3d.tsx` entrega ao three.js.
 * Assim o three.js só entra no `Modelo3d.tsx`, por `import('three')` (R7), e a conta se testa no Node (`303.61`).
 *
 * A cena: x para leste, y para cima (cota × exagero), z para o SUL (−norte) — a convenção destra do three.js,
 * com y para cima; x e −z em m no plano local das verticais úteis (`planoLocal`, o mesmo da grade do motor).
 * A textura (imagem do satélite, fatia, cerca) vai com `flipY` desligado: a linha 0 dos dados é o topo da
 * imagem, em v = 0.
 *
 * MODELO INTERPRETATIVO, FORA DO RN-252: entre as verticais o valor é ESTIMADO; a coluna de cada vertical é
 * a zona MEDIDA (`r.sbtn`). O voxel é centrado no nó da grade — ESCOLHA DA TELA, SEM FONTE [esp.voxel-centro];
 * a malha do terreno, no passo da grade, ancorada nas verticais [esp.terreno-malha]; o limite de voxels
 * [esp.voxels-limite] (a lista é a `ESCOLHAS_DA_ANALISE` do `espacialDaCampanha.ts`).
 */
import { ZONAS_SBTN, idw } from '../motor';
import type { PlanoLocal } from '../motor';
import { numeroBr } from './escalas';
import {
  FITA_DO_3D, PASSOS_DA_GRADE, POTENCIA_DAS_SUPERFICIES, VOXELS_NO_MAXIMO, classeDoValor, classesDaEscala, colunaPrincipal, grandezaDaSecao,
  normalizarUnidade,
} from './espacialDaCampanha';
import type {
  GrandezaDaSecao, UnidadesNaGrade, LimitesDaImagem, LinhaDeCorte, RespostaDaGrade, RespostaDaSecao, SobreposicoesDasVerticais, VerticalNoPedido,
} from './espacialDaCampanha';

/** As cores do modelo, lidas dos tokens (`--ts-sbtn-N` e `--ts-escala-N`) pelo `corDoToken`. */
export interface CoresDoModelo {
  /** A cor de cada zona SBTn (1–9), RGB 0–255. */
  zonas: Readonly<Record<number, [number, number, number]>>;
  /** As 7 cores das classes da escala contínua, da classe 1 à 7. */
  escala: readonly [number, number, number][];
}

/** Uma cor de um token lido do `getComputedStyle`: «#RRGGBB», «#RGB», «rgb(r, g, b)» ou «rgba(r, g, b, a)»; null no resto. */
export function corDoToken(valorCss: string): [number, number, number] | null {
  const t = (valorCss ?? '').trim();
  const h = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(t);
  if (h) {
    const d = h[1].length === 3 ? h[1].split('').map((c) => c + c).join('') : h[1];
    return [parseInt(d.slice(0, 2), 16), parseInt(d.slice(2, 4), 16), parseInt(d.slice(4, 6), 16)];
  }
  const r = /^rgba?\(\s*([\d.]+)\s*[,\s]\s*([\d.]+)\s*[,\s]\s*([\d.]+)\s*(?:[,/]\s*[\d.]+%?\s*)?\)$/i.exec(t);
  if (r) {
    const canal = (x: string): number => Math.min(255, Math.max(0, Math.round(Number(x))));
    const c: [number, number, number] = [canal(r[1]), canal(r[2]), canal(r[3])];
    return c.every(Number.isFinite) ? c : null;
  }
  return null;
}

/** y de Mercator (sem escala) de uma latitude em graus. */
const mercatorY = (lat: number): number => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));

/**
 * O UV de um ponto na imagem do terreno (Web Mercator, como as telhas): (0, 0) no canto noroeste, (1, 1) no
 * sudeste; u cresce para leste, v para o sul.
 */
export function uvNaImagem(lat: number, lon: number, l: LimitesDaImagem): [number, number] {
  const u = (lon - l.oeste) / (l.leste - l.oeste);
  const yN = mercatorY(l.norte);
  const v = (yN - mercatorY(lat)) / (yN - mercatorY(l.sul));
  return [u, v];
}

/** As posições de um eixo da malha do terreno: os dois limites e os nós do passo ancorado entre eles. */
function eixoDaMalha(de: number, ate: number, ancora: number, passo: number): number[] {
  const nos = [de];
  if (passo > 0 && Number.isFinite(passo) && Number.isFinite(ancora)) {
    const folga = passo * 1e-6;
    let k = Math.ceil((de - ancora) / passo - 1e-9);
    // Guarda contra o passo minúsculo: no máximo 4.096 nós por eixo.
    for (let n = 0; n < 4096; n++, k++) {
      const x = ancora + k * passo;
      if (x >= ate - folga) break;
      if (x > de + folga) nos.push(x);
    }
  }
  nos.push(ate);
  return nos;
}

/**
 * O terreno do modelo: a malha cobre os limites da imagem, com o passo da grade em planta ancorado no canto
 * sudoeste da caixa das verticais [esp.terreno-malha] — a vertical num múltiplo do passo cai num vértice —, e a
 * altura de cada vértice é a superfície das bocas (IDW, potência 2 [esp.superficies-idw]) × o exagero. O UV
 * (Mercator) só com a imagem.
 */
export function terrenoDoModelo(g: RespostaDaGrade, x: { exagero: number; plano: PlanoLocal; limites: LimitesDaImagem; comImagem: boolean }):
{ posicoes: Float32Array; uv: Float32Array | null; indices: Uint32Array } {
  const so = x.plano.paraPlano(x.limites.sul, x.limites.oeste);
  const ne = x.plano.paraPlano(x.limites.norte, x.limites.leste);
  if (![so.x, so.y, ne.x, ne.y].every(Number.isFinite) || !g.verticais.length) {
    return { posicoes: new Float32Array(0), uv: x.comImagem ? new Float32Array(0) : null, indices: new Uint32Array(0) };
  }
  let ax = Infinity, ay = Infinity;
  for (const v of g.verticais) { ax = Math.min(ax, v.x); ay = Math.min(ay, v.y); }
  const xs = eixoDaMalha(so.x, ne.x, ax, g.grade.passo.x);
  const ys = eixoDaMalha(so.y, ne.y, ay, g.grade.passo.y);
  const bocas = g.verticais.map((v) => ({ p: { x: v.x, y: v.y }, valor: v.boca }));
  const nX = xs.length, nY = ys.length;
  const posicoes = new Float32Array(nX * nY * 3);
  const uv = x.comImagem ? new Float32Array(nX * nY * 2) : null;
  for (let j = 0; j < nY; j++) {
    for (let i = 0; i < nX; i++) {
      const k = j * nX + i;
      const altura = idw(bocas, { x: xs[i], y: ys[j] }, POTENCIA_DAS_SUPERFICIES);
      posicoes[3 * k] = xs[i];
      posicoes[3 * k + 1] = altura * x.exagero;
      posicoes[3 * k + 2] = -ys[j];
      if (uv) {
        const geo = x.plano.paraGeo({ x: xs[i], y: ys[j] });
        const [u, v] = uvNaImagem(geo.latitude, geo.longitude, x.limites);
        uv[2 * k] = u; uv[2 * k + 1] = v;
      }
    }
  }
  const indices = new Uint32Array((nX - 1) * (nY - 1) * 6);
  let n = 0;
  for (let j = 0; j + 1 < nY; j++) {
    for (let i = 0; i + 1 < nX; i++) {
      const a = j * nX + i, b = a + 1, c = a + nX, d = c + 1;
      // Anti-horário visto de cima (y para cima, z para o sul): a face aponta para o céu.
      indices[n++] = a; indices[n++] = b; indices[n++] = c;
      indices[n++] = b; indices[n++] = d; indices[n++] = c;
    }
  }
  return { posicoes, uv, indices };
}

/**
 * As colunas das verticais: um segmento por leitura (da leitura à seguinte; o último, de comprimento zero na
 * última leitura), com a zona MEDIDA (`r.sbtn`). Posições em pares (topo, base) de x, y, z.
 */
export function colunasDoModelo(vs: readonly VerticalNoPedido[], x: { exagero: number; plano: PlanoLocal }):
{ codigo: string; posicoes: Float32Array; zonas: Int8Array }[] {
  return vs.map((v) => {
    const q = x.plano.paraPlano(v.latitude, v.longitude);
    const n = v.z.length;
    const posicoes = new Float32Array(6 * n);
    for (let i = 0; i < n; i++) {
      const topo = v.cota - v.z[i];
      const base = v.cota - v.z[Math.min(i + 1, n - 1)];
      posicoes.set([q.x, topo * x.exagero, -q.y, q.x, base * x.exagero, -q.y], 6 * i);
    }
    return { codigo: v.codigo, posicoes, zonas: Int8Array.from(v.zonaMedida.subarray(0, n)) };
  });
}

/**
 * A cor pela ZONA só no SBTn; o Su e as outras contínuas vão pela escala `--ts-escala-1…7` (as 7 classes), como na
 * seção 2D (R5). No Su a zona continua servindo, mas só ao recorte argiloso (`zonaArgilosa`), nunca à cor: a cor
 * da zona passaria por valor de Su.
 */
export const pintaPelaZona = (grandeza: GrandezaDaSecao): boolean => grandeza === 'sbtn';

/** O domínio (mín, máx) dos valores marcados (os visíveis, os pintados); numa escala log, só os > 0. */
function dominioDosMarcados(valores: ArrayLike<number>, marcados: ArrayLike<number>, escala: 'linear' | 'log'): [number, number] {
  let mn = Infinity, mx = -Infinity;
  for (let k = 0; k < valores.length; k++) {
    const v = valores[k];
    if (!marcados[k] || !Number.isFinite(v) || (escala === 'log' && !(v > 0))) continue;
    if (v < mn) mn = v;
    if (v > mx) mx = v;
  }
  return [mn, mx];
}

/**
 * As classes da escala contínua do modelo, calculadas UMA vez sobre o domínio dos visíveis da grade e passadas
 * às fatias, à cerca, aos voxels e à legenda — a mesma cor vale o mesmo intervalo em todo o 3D. No SBTn, nada
 * (a cor é a da zona): `limites` vazio.
 */
export function classesDaGrade(g: RespostaDaGrade): { limites: number[]; dominio: [number, number] } {
  if (pintaPelaZona(g.grandeza)) return { limites: [], dominio: [NaN, NaN] };
  const valores = g.valores[colunaPrincipal(g.grandeza)] ?? g.grade.valores;
  const escala = grandezaDaSecao(g.grandeza).escala;
  const dominio = dominioDosMarcados(valores, g.visivel, escala);
  return { limites: classesDaEscala(dominio, escala), dominio };
}

/** A cor de cada nó visível: a da zona (só SBTn) ou a da classe da escala contínua, pelos limites dados. */
function pintorDaGrade(g: RespostaDaGrade, cores: CoresDoModelo, limitesDados?: readonly number[]): (k: number) => [number, number, number] | null {
  if (pintaPelaZona(g.grandeza) && g.zonas) {
    const zonas = g.zonas;
    return (k) => (g.visivel[k] ? cores.zonas[zonas[k]] ?? null : null);
  }
  const valores = g.valores[colunaPrincipal(g.grandeza)] ?? g.grade.valores;
  const limites = limitesDados ?? classesDaGrade(g).limites;
  return (k) => (g.visivel[k] ? cores.escala[classeDoValor(valores[k], limites) - 1] ?? null : null);
}

/** Um item da legenda de cores do 3D: a cor (a zona ou a classe), o número e o texto — a cor nunca vai sozinha (P-UI-15). */
export interface ItemDaLegenda { tipo: 'zona' | 'classe'; numero: number; texto: string }

/** As casas decimais que escrevem os limites das classes sem perder dígito (no máximo 3). */
function casasDosLimites(limites: readonly number[]): number {
  let casas = 0;
  for (const v of limites) {
    if (!Number.isFinite(v)) continue;
    let c = 0;
    while (c < 3 && Math.abs(v - Number(v.toFixed(c))) > 1e-9 * Math.max(1, Math.abs(v))) c++;
    casas = Math.max(casas, c);
  }
  return casas;
}

/**
 * A legenda de cores do modelo: no SBTn, as zonas presentes entre os visíveis (número e nome); nas contínuas
 * (o Su incluso), as 7 classes com os limites e a unidade («classe 3 · 20 a 30 kPa»; a última, quando o domínio
 * passa do último limite, «≥ …»). A mesma lista vai na tela e na captura.
 */
export function legendaDoModelo(g: RespostaDaGrade, classes: { limites: readonly number[]; dominio: readonly [number, number] }):
{ titulo: string; itens: ItemDaLegenda[] } {
  const def = grandezaDaSecao(g.grandeza);
  if (pintaPelaZona(g.grandeza)) {
    const presentes = new Set<number>();
    if (g.zonas) for (let k = 0; k < g.visivel.length; k++) if (g.visivel[k] && g.zonas[k] > 0) presentes.add(g.zonas[k]);
    const nomes = ZONAS_SBTN as unknown as Readonly<Record<number, { nome: string }>>;
    return {
      titulo: 'Zonas SBTn estimadas (Robertson 1990)',
      itens: [...presentes].sort((a, b) => a - b).map((z) => ({ tipo: 'zona' as const, numero: z, texto: `${z} · ${nomes[z]?.nome ?? 'zona'}` })),
    };
  }
  const unidade = def.unidade ? ` ${def.unidade}` : '';
  const titulo = `${def.rotulo}${def.unidade ? ` (${def.unidade})` : ''} — 7 classes`;
  const l = classes.limites;
  if (!l.length || !Number.isFinite(l[0])) return { titulo, itens: [] };
  const casas = casasDosLimites(l);
  const n = (v: number): string => numeroBr(v, casas);
  if (l.length === 1) return { titulo, itens: [{ tipo: 'classe', numero: 1, texto: `classe 1 · ${n(l[0])}${unidade}` }] };
  const aberta = Number.isFinite(classes.dominio[1]) && classes.dominio[1] > l[l.length - 1] * (1 + 1e-9);
  const itens: ItemDaLegenda[] = [];
  for (let k = 1; k < l.length; k++) {
    const texto = k === l.length - 1 && aberta ? `≥ ${n(l[k - 1])}` : `${n(l[k - 1])} a ${n(l[k])}`;
    itens.push({ tipo: 'classe', numero: k, texto: `classe ${k} · ${texto}${unidade}` });
  }
  return { titulo, itens };
}

/**
 * Uma fatia da grade: N–S (um plano em x, as colunas de sul a norte), E–O (um plano em y, de oeste a leste) ou
 * em planta (uma cota, as linhas de norte a sul). A fatia passa pelo NÓ mais perto da posição pedida (m para
 * leste, m para norte ou a cota) — `indice` diz qual [esp.voxel-centro]; nas verticais, a linha 0 é a cota mais
 * alta. Os cantos (topo-esquerda, topo-direita, base-direita, base-esquerda) se estendem meia célula além dos nós
 * extremos. A cor é a da zona (só SBTn) ou da classe (`limites`, os de `classesDaGrade`; sem eles, calculados
 * aqui), alfa 0 fora do visível.
 */
export function fatiaDoModelo(g: RespostaDaGrade, tipo: 'ns' | 'eo' | 'planta', posicao: number,
  x: { exagero: number; cores: CoresDoModelo; limites?: readonly number[] }):
{ cantos: Float32Array; largura: number; altura: number; rgba: Uint8ClampedArray; indice: number } {
  const { origem, passo, n } = g.grade;
  const vazio = { cantos: new Float32Array(12), largura: 0, altura: 0, rgba: new Uint8ClampedArray(0), indice: -1 };
  if (!n.x || !n.y || !n.z || !Number.isFinite(posicao)) return vazio;
  const pintar = pintorDaGrade(g, x.cores, x.limites);
  const perto = (de: number, p: number, total: number): number => Math.min(total - 1, Math.max(0, Math.round((posicao - de) / p)));
  const x0 = origem.x - passo.x / 2, x1 = origem.x + (n.x - 0.5) * passo.x;
  const y0 = origem.y - passo.y / 2, y1 = origem.y + (n.y - 0.5) * passo.y;
  const c0 = (origem.cota - passo.z / 2) * x.exagero, c1 = (origem.cota + (n.z - 0.5) * passo.z) * x.exagero;
  let largura: number, altura: number, indice: number, cantos: number[];
  let no: (col: number, lin: number) => number;
  if (tipo === 'ns') {
    indice = perto(origem.x, passo.x, n.x);
    largura = n.y; altura = n.z;
    const xx = origem.x + indice * passo.x;
    cantos = [xx, c1, -y0, xx, c1, -y1, xx, c0, -y1, xx, c0, -y0];
    no = (col, lin) => indice + n.x * (col + n.y * (n.z - 1 - lin));
  } else if (tipo === 'eo') {
    indice = perto(origem.y, passo.y, n.y);
    largura = n.x; altura = n.z;
    const yy = -(origem.y + indice * passo.y);
    cantos = [x0, c1, yy, x1, c1, yy, x1, c0, yy, x0, c0, yy];
    no = (col, lin) => col + n.x * (indice + n.y * (n.z - 1 - lin));
  } else {
    indice = perto(origem.cota, passo.z, n.z);
    largura = n.x; altura = n.y;
    const cc = (origem.cota + indice * passo.z) * x.exagero;
    cantos = [x0, cc, -y1, x1, cc, -y1, x1, cc, -y0, x0, cc, -y0];
    no = (col, lin) => col + n.x * ((n.y - 1 - lin) + n.y * indice);
  }
  const rgba = new Uint8ClampedArray(largura * altura * 4);
  for (let lin = 0; lin < altura; lin++) {
    for (let col = 0; col < largura; col++) {
      const cor = pintar(no(col, lin));
      if (!cor) continue;
      const p = 4 * (lin * largura + col);
      rgba[p] = cor[0]; rgba[p + 1] = cor[1]; rgba[p + 2] = cor[2]; rgba[p + 3] = 255;
    }
  }
  return { cantos: Float32Array.from(cantos), largura, altura, rgba, indice };
}

/**
 * A cerca: a seção 2D (a MESMA `RespostaDaSecao` da tela) em pé ao longo da linha — dois vértices por coluna
 * (a cota mais alta e a mais baixa da seção), no ponto da polilinha à distância da coluna; a textura é a da seção,
 * célula a célula (linha 0 = a cota mais alta), alfa 0 fora do pintado. A cor é a da zona só no SBTn; no Su e
 * nas contínuas, a da classe da escala da grandeza — pelos `limites` dados (os da grade, `classesDaGrade`, para a
 * cerca e as fatias falarem a mesma cor) ou, sem eles, pelos valores pintados da seção.
 */
export function cercaDoModelo(s: RespostaDaSecao, linha: LinhaDeCorte['pontos'],
  x: { exagero: number; plano: PlanoLocal; cores: CoresDoModelo; grandeza: GrandezaDaSecao; limites?: readonly number[] }):
{ posicoes: Float32Array; uv: Float32Array; indices: Uint32Array; rgba: Uint8ClampedArray; largura: number; altura: number } {
  const d = s.secao.distancias, cotas = s.secao.cotas;
  const nd = d.length, nc = cotas.length;
  const poli = linha.map((q) => x.plano.paraPlano(q.latitude, q.longitude));
  const acumulado = [0];
  for (let i = 1; i < poli.length; i++) acumulado.push(acumulado[i - 1] + Math.hypot(poli[i].x - poli[i - 1].x, poli[i].y - poli[i - 1].y));
  const pontoNa = (dist: number): { x: number; y: number } => {
    if (!poli.length) return { x: NaN, y: NaN };
    let t = 0;
    while (t + 2 < poli.length && acumulado[t + 1] < dist) t++;
    const a = poli[t], b = poli[Math.min(t + 1, poli.length - 1)];
    const l = acumulado[Math.min(t + 1, poli.length - 1)] - acumulado[t];
    const f = l > 0 ? Math.min(Math.max((dist - acumulado[t]) / l, 0), 1) : 0;
    return { x: a.x + f * (b.x - a.x), y: a.y + f * (b.y - a.y) };
  };
  const posicoes = new Float32Array(nd * 2 * 3);
  const uv = new Float32Array(nd * 2 * 2);
  const topo = nc ? cotas[0] * x.exagero : 0, base = nc ? cotas[nc - 1] * x.exagero : 0;
  for (let i = 0; i < nd; i++) {
    const q = pontoNa(d[i]);
    posicoes.set([q.x, topo, -q.y, q.x, base, -q.y], 6 * i);
    const u = (i + 0.5) / nd;
    uv.set([u, 0.5 / nc, u, (nc - 0.5) / nc], 4 * i);
  }
  const indices = new Uint32Array(Math.max(0, nd - 1) * 6);
  for (let i = 0; i + 1 < nd; i++) {
    const a = 2 * i, b = a + 1, c = a + 2, e = a + 3;
    indices.set([a, b, c, b, e, c], 6 * i);
  }
  const rgba = new Uint8ClampedArray(nd * nc * 4);
  const zonas = pintaPelaZona(x.grandeza) ? s.zonas : null;
  const valores = s.secao.valores;
  const limites = zonas ? [] : x.limites ?? classesDaEscala(dominioDosMarcados(valores, s.pintar, grandezaDaSecao(x.grandeza).escala), grandezaDaSecao(x.grandeza).escala);
  for (let k = 0; k < nd * nc; k++) {
    if (!s.pintar[k]) continue;
    const cor = zonas ? x.cores.zonas[zonas[k]] : x.cores.escala[classeDoValor(valores[k], limites) - 1];
    if (!cor) continue;
    rgba[4 * k] = cor[0]; rgba[4 * k + 1] = cor[1]; rgba[4 * k + 2] = cor[2]; rgba[4 * k + 3] = 255;
  }
  return { posicoes, uv, indices, rgba, largura: nd, altura: nc };
}

/* ── As fitas das verticais (o pedido do Humberto de 16/10/2026) ─────── */

/**
 * A largura toda da fita de cada vertical, m: 0,4 × a menor distância em planta entre dois lugares distintos (as
 * verticais no mesmo lugar contam uma vez), entre 1 e 20 m; com um lugar só, 5 m — ESCOLHA DA TELA, SEM FONTE
 * [esp.fita-3d]. Com o exagero horizontal ×1 e o raio pequeno diante da distância, as fitas de duas vizinhas não se
 * cobrem; o raio (a fita começa na face da coluna) e o exagero maiores podem fazê-las se cobrir.
 */
export function larguraDaFita(pontos: readonly { x: number; y: number }[]): number {
  let menor = Infinity;
  for (let i = 0; i < pontos.length; i++) {
    for (let j = i + 1; j < pontos.length; j++) {
      const d = Math.hypot(pontos[i].x - pontos[j].x, pontos[i].y - pontos[j].y);
      if (Number.isFinite(d) && d > 0.01 && d < menor) menor = d;
    }
  }
  if (!Number.isFinite(menor)) return FITA_DO_3D.semPar;
  return Math.min(FITA_DO_3D.maximo, Math.max(FITA_DO_3D.minimo, FITA_DO_3D.fracao * menor));
}

/**
 * A fita de uma vertical no 3D: no lugar dela (x leste, y norte, no plano local), em coordenadas PRÓPRIAS — u de 0
 * (o início da escala) à largura; a cota em m (sem o exagero vertical, que é a escala do grupo); 0 no terceiro eixo.
 * O `Modelo3d` a põe no lado escolhido da coluna (leste de partida, onda 347 §4.7): u = 0 na face da coluna, u para fora.
 */
export interface FitaDaVertical {
  codigo: string;
  x: number;
  y: number;
  tipo: 'serie' | 'zona';
  /** Os vértices (u, cota, 0). */
  posicoes: Float32Array;
  indices: Uint32Array;
  /** Na zona, a zona de cada vértice (a cor); na série, null. */
  zonas: Int8Array | null;
  /** Na série, o traço do perfil em pares de pontos (segmentos), (u, cota, 0); na zona, vazio. */
  traco: Float32Array;
  /** A moldura: o eixo da vertical e a borda da largura toda, da boca à última leitura, em pares (segmentos). */
  moldura: Float32Array;
  /** A escala da fita (o início no eixo, o fim na largura toda); na zona, [0, 1]. */
  dominio: [number, number];
  largura: number;
}

/**
 * As fitas das verticais ligadas, das MESMAS faixas da seção 2D (`sobreposicoesDasVerticais`): na série, cada leitura
 * com valor vai do eixo (u = 0, o início da escala) a u = largura × (valor − início)/(fim − início) — no eixo log, a
 * fração dos logaritmos (onda 347) —, presa à largura (o valor fora dos limites fixos fica na borda), e
 * leituras seguidas com valor fecham um quadrilátero (a leitura sem valor quebra a fita e o traço); na zona, um
 * retângulo da largura toda por trecho da mesma zona (`trechosDeZona`), com a zona em cada vértice. A vertical sem
 * coordenada fica de fora (não há onde pôr a fita). O exagero horizontal (onda 347 §4.4, de ×1 a ×10) multiplica só a
 * largura: a escala decide o que ocupa a largura, e o exagero quanto ela vale em metros. Pura: o three.js só recebe os buffers.
 */
export function fitasDasVerticais(
  s: SobreposicoesDasVerticais,
  x: { plano: PlanoLocal; ligadas: ReadonlySet<string>; largura: number; exageroHorizontal?: number },
): FitaDaVertical[] {
  const L = x.largura * (x.exageroHorizontal ?? 1);
  const saida: FitaDaVertical[] = [];
  for (const v of s.verticais) {
    if (!x.ligadas.has(v.codigo) || v.latitude === null || v.longitude === null) continue;
    const q = x.plano.paraPlano(v.latitude, v.longitude);
    if (!Number.isFinite(q.x) || !Number.isFinite(q.y)) continue;
    const fundo = v.cotas.length ? v.cotas[v.cotas.length - 1] : v.cota;
    const moldura = Float32Array.from([0, v.cota, 0, 0, fundo, 0, L, v.cota, 0, L, fundo, 0, 0, v.cota, 0, L, v.cota, 0, 0, fundo, 0, L, fundo, 0]);
    if (s.grandeza.tipo === 'zona') {
      const posicoes = new Float32Array(v.trechos.length * 12);
      const indices = new Uint32Array(v.trechos.length * 6);
      const zonas = new Int8Array(v.trechos.length * 4);
      v.trechos.forEach((t, i) => {
        posicoes.set([0, t.topo, 0, L, t.topo, 0, L, t.base, 0, 0, t.base, 0], 12 * i);
        const a = 4 * i;
        indices.set([a, a + 3, a + 1, a + 1, a + 3, a + 2], 6 * i);
        zonas.fill(t.zona, a, a + 4);
      });
      saida.push({ codigo: v.codigo, x: q.x, y: q.y, tipo: 'zona', posicoes, indices, zonas, traco: new Float32Array(0), moldura, dominio: [0, 1], largura: L });
      continue;
    }
    const [d0, d1] = v.dominio;
    // No eixo log (onda 347, R12), a fração é a dos logaritmos; o valor ≤ 0 não tem lugar e quebra a fita.
    const log = s.tipo === 'log' && d0 > 0 && d1 > 0;
    const a0 = log ? Math.log10(d0) : d0;
    const amplitude = (log ? Math.log10(d1) : d1) - a0;
    const n = v.cotas.length;
    const posicoes = new Float32Array(n * 6);
    const larguraDe = (val: number): number => (amplitude > 0 ? Math.min(1, Math.max(0, ((log ? Math.log10(val) : val) - a0) / amplitude)) : 0) * L;
    const comValor: boolean[] = [];
    for (let i = 0; i < n; i++) {
      const val = v.valores[i];
      const ok = Number.isFinite(val) && Number.isFinite(v.cotas[i]) && (!log || val > 0);
      comValor.push(ok);
      const u = ok ? larguraDe(val) : 0;
      posicoes.set([0, v.cotas[i], 0, u, v.cotas[i], 0], 6 * i);
    }
    const indices: number[] = [];
    const traco: number[] = [];
    for (let i = 0; i + 1 < n; i++) {
      if (!comValor[i] || !comValor[i + 1]) continue;
      const a = 2 * i, b = a + 1, c = a + 2, d = a + 3;
      indices.push(a, c, b, b, c, d);
      traco.push(posicoes[6 * i + 3], posicoes[6 * i + 4], 0, posicoes[6 * i + 9], posicoes[6 * i + 10], 0);
    }
    saida.push({
      codigo: v.codigo, x: q.x, y: q.y, tipo: 'serie', posicoes, indices: Uint32Array.from(indices), zonas: null,
      traco: Float32Array.from(traco), moldura, dominio: [d0, d1], largura: L,
    });
  }
  return saida;
}

/**
 * Os voxels a desenhar: os nós visíveis cuja zona (só SBTn) ou classe da escala (o Su e as contínuas, pelos
 * `limites` de `classesDaGrade`) está no conjunto pedido, com o centro na cena. Acima do limite (250.000 de partida [esp.voxels-limite]) não desenha
 * nenhum: `excedeu`, `n` = quantos seriam e o passo sugerido — o seguinte da série da grade em planta
 * (1 · 2 · 5 · 10 m) e, acabada a série, o dobro do vertical, até a estimativa caber.
 */
export function voxelsDoModelo(g: RespostaDaGrade, x: { zonas: ReadonlySet<number>; exagero: number; limite?: number; limites?: readonly number[] }):
{ centros: Float32Array; zonas: Int8Array; n: number; excedeu: boolean; passoSugerido: { x: number; y: number; z: number } | null } {
  const limite = x.limite ?? VOXELS_NO_MAXIMO;
  const { origem, passo, n } = g.grade;
  const total = n.x * n.y * n.z;
  let chave: (k: number) => number;
  if (pintaPelaZona(g.grandeza) && g.zonas) {
    const zonas = g.zonas;
    chave = (k) => zonas[k];
  } else {
    const valores = g.valores[colunaPrincipal(g.grandeza)] ?? g.grade.valores;
    const limites = x.limites ?? classesDaGrade(g).limites;
    chave = (k) => classeDoValor(valores[k], limites);
  }
  let conta = 0;
  for (let k = 0; k < total; k++) if (g.visivel[k] && x.zonas.has(chave(k))) conta++;
  if (conta > limite) {
    let sugerido = { ...passo };
    for (let volta = 0; volta < 32; volta++) {
      const seguinte = PASSOS_DA_GRADE.find((p) => p > sugerido.x + 1e-9);
      sugerido = seguinte !== undefined ? { x: seguinte, y: seguinte, z: sugerido.z } : { ...sugerido, z: sugerido.z * 2 };
      const estimado = (conta * passo.x * passo.y * passo.z) / (sugerido.x * sugerido.y * sugerido.z);
      if (estimado <= limite) break;
    }
    return { centros: new Float32Array(0), zonas: new Int8Array(0), n: conta, excedeu: true, passoSugerido: sugerido };
  }
  const centros = new Float32Array(conta * 3);
  const zonas = new Int8Array(conta);
  let i = 0;
  for (let iz = 0; iz < n.z; iz++) {
    for (let iy = 0; iy < n.y; iy++) {
      for (let ix = 0; ix < n.x; ix++) {
        const k = ix + n.x * (iy + n.y * iz);
        if (!g.visivel[k]) continue;
        const z = chave(k);
        if (!x.zonas.has(z)) continue;
        centros[3 * i] = origem.x + ix * passo.x;
        centros[3 * i + 1] = (origem.cota + iz * passo.z) * x.exagero;
        centros[3 * i + 2] = -(origem.y + iy * passo.y);
        zonas[i] = z;
        i++;
      }
    }
  }
  return { centros, zonas, n: conta, excedeu: false, passoSugerido: null };
}

/* ── As camadas geotécnicas (onda 347, spec 2026-10-22 §4.6) ───────────── */

/** As unidades escondidas, pela chave (`normalizarUnidade`): a lista vem em nomes ou em chaves, tanto faz. */
const indicesEscondidos = (u: Pick<UnidadesNaGrade, 'unidades'>, escondidas: ReadonlySet<string>): Uint8Array => {
  const chaves = new Set([...escondidas].map(normalizarUnidade));
  return Uint8Array.from(u.unidades, (nome) => (chaves.has(normalizarUnidade(nome)) ? 1 : 0));
};

/**
 * Os voxels de unidade (§4.6, «Blocos»): o centro de cada nó com unidade que não está escondida (x leste, cota × exagero,
 * −norte; centrado no nó, como os voxels de hoje [esp.voxel-centro]) e a unidade dele (a posição em `u.unidades`).
 */
export function voxelsDeUnidade(g: Pick<RespostaDaGrade, 'grade'>, u: Pick<UnidadesNaGrade, 'unidades' | 'indice'>,
  x: { escondidas: ReadonlySet<string>; exagero: number }): { centros: Float32Array; unidade: Int16Array } {
  const { origem, passo, n } = g.grade;
  const fora = indicesEscondidos(u, x.escondidas);
  let conta = 0;
  for (let k = 0; k < u.indice.length; k++) if (u.indice[k] >= 0 && !fora[u.indice[k]]) conta++;
  const centros = new Float32Array(conta * 3);
  const unidade = new Int16Array(conta);
  let i = 0;
  for (let iz = 0; iz < n.z; iz++) {
    for (let iy = 0; iy < n.y; iy++) {
      for (let ix = 0; ix < n.x; ix++) {
        const un = u.indice[ix + n.x * (iy + n.y * iz)];
        if (un < 0 || fora[un]) continue;
        centros[3 * i] = origem.x + ix * passo.x;
        centros[3 * i + 1] = (origem.cota + iz * passo.z) * x.exagero;
        centros[3 * i + 2] = -(origem.y + iy * passo.y);
        unidade[i++] = un;
      }
    }
  }
  return { centros, unidade };
}

/**
 * Os contatos (§4.6, «Só contatos»): as faces entre dois nós vizinhos (nas três direções) com unidades diferentes, no meio do
 * caminho entre eles, do tamanho da célula — dois triângulos (18 números) por face —, na cor da unidade de baixo: na vertical, a
 * do nó de baixo; de lado, a mais funda na lista. A face cuja unidade de cor está escondida não sai.
 */
export function facesDeContato(g: Pick<RespostaDaGrade, 'grade'>, u: Pick<UnidadesNaGrade, 'unidades' | 'indice'>,
  x: { escondidas: ReadonlySet<string>; exagero: number }): { posicoes: Float32Array; unidade: Int16Array } {
  const { origem, passo, n } = g.grade;
  const fora = indicesEscondidos(u, x.escondidas);
  const pos: number[] = [];
  const uni: number[] = [];
  const ex = x.exagero;
  // Uma face de centro (cx, cy, cz) no plano local (x leste, y norte, cota), normal ao eixo `eixo`, com os meios-lados dados.
  const face = (cx: number, cy: number, cz: number, eixo: 'x' | 'y' | 'z', un: number): void => {
    const hx = passo.x / 2, hy = passo.y / 2, hz = passo.z / 2;
    const cantos: [number, number, number][] = eixo === 'z'
      ? [[cx - hx, cy - hy, cz], [cx + hx, cy - hy, cz], [cx + hx, cy + hy, cz], [cx - hx, cy + hy, cz]]
      : eixo === 'x'
        ? [[cx, cy - hy, cz - hz], [cx, cy + hy, cz - hz], [cx, cy + hy, cz + hz], [cx, cy - hy, cz + hz]]
        : [[cx - hx, cy, cz - hz], [cx + hx, cy, cz - hz], [cx + hx, cy, cz + hz], [cx - hx, cy, cz + hz]];
    for (const c of [0, 1, 2, 0, 2, 3]) pos.push(cantos[c][0], cantos[c][2] * ex, -cantos[c][1]);
    uni.push(un);
  };
  const em = (ix: number, iy: number, iz: number): number => u.indice[ix + n.x * (iy + n.y * iz)];
  for (let iz = 0; iz < n.z; iz++) {
    for (let iy = 0; iy < n.y; iy++) {
      for (let ix = 0; ix < n.x; ix++) {
        const a = em(ix, iy, iz);
        if (a < 0) continue;
        const x0 = origem.x + ix * passo.x, y0 = origem.y + iy * passo.y, z0 = origem.cota + iz * passo.z;
        // Para cima: a de baixo é este nó.
        if (iz + 1 < n.z) { const b = em(ix, iy, iz + 1); if (b >= 0 && b !== a && !fora[a]) face(x0, y0, z0 + passo.z / 2, 'z', a); }
        // De lado (leste e norte): a mais funda da lista.
        if (ix + 1 < n.x) { const b = em(ix + 1, iy, iz); const c = Math.max(a, b); if (b >= 0 && b !== a && !fora[c]) face(x0 + passo.x / 2, y0, z0, 'x', c); }
        if (iy + 1 < n.y) { const b = em(ix, iy + 1, iz); const c = Math.max(a, b); if (b >= 0 && b !== a && !fora[c]) face(x0, y0 + passo.y / 2, z0, 'y', c); }
      }
    }
  }
  return { posicoes: Float32Array.from(pos), unidade: Int16Array.from(uni) };
}

/**
 * O token da cor de uma unidade (§4.6): o da zona SBTn predominante dela (`--ts-sbtn-N`, as cores das colunas); sem zona, o cinza
 * `--ts-tinta-3`. A cor nunca vai sozinha: a lista e a legenda escrevem o nome da unidade.
 */
export function corDaUnidade(_unidade: string, zonaPredominante: number | null): string {
  return zonaPredominante !== null && zonaPredominante >= 1 && zonaPredominante <= 9 ? `--ts-sbtn-${zonaPredominante}` : '--ts-tinta-3';
}
